import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { awaitJSON, SheetsLambdaFetch } from '@src/utils/fetch-utils';
import { useSetError } from '@src/redux/modals';
import { QueryRow, QueryRowParams } from './query-exporter-constants';

const QUERY_KEY = ['queryExporterRows'];

const fetchQueryExporterRows = async (): Promise<QueryRow[]> =>
  awaitJSON<QueryRow[]>(await SheetsLambdaFetch('/get_export_query_sheets'));

const saveQueryExporterRows = async (body: QueryRowParams): Promise<void> => {
  await SheetsLambdaFetch<QueryRowParams>('/update_export_query_sheets', { method: 'POST', body });
};

const runQueryExportRow = async (id: number): Promise<void> => {
  await SheetsLambdaFetch('/run_export_query_sheets', { method: 'POST', body: { id } });
};

// Updates the local table state synchronously as soon as a row's edit modal is saved --
// without this, the table would otherwise sit showing stale data for a network round-trip
// after every save.
export const applyQueryRowChanges = (
  rows: QueryRow[],
  { insert, update, delete: deleted }: QueryRowParams
): QueryRow[] => {
  const deletedIds = new Set(deleted.map(row => row.id));
  const updatedById = new Map(update.map(row => [row.id, row]));
  // A newly-added row has no server-assigned id yet -- give it a unique negative placeholder so
  // two adds in flight at once don't both key to getRowId's String(undefined) and collapse into
  // one row until the refetch settles.
  const insertedWithTempIds = insert.map(row => ({
    ...row,
    id: row.id ?? -Math.floor(Math.random() * 1_000_000_000) - 1
  }));
  return [
    ...rows.filter(row => !deletedIds.has(row.id)).map(row => updatedById.get(row.id) ?? row),
    ...insertedWithTempIds
  ];
};

export const useQueryExporterData = () => {
  const queryClient = useQueryClient();
  const setError = useSetError();
  const query = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: QUERY_KEY,
    queryFn: fetchQueryExporterRows
  });

  const saveMutation = useMutation({
    mutationFn: saveQueryExporterRows,
    onMutate: (params: QueryRowParams) => {
      const previousRows = queryClient.getQueryData<QueryRow[]>(QUERY_KEY);
      queryClient.setQueryData<QueryRow[]>(QUERY_KEY, rows =>
        applyQueryRowChanges(rows ?? [], params)
      );
      return { previousRows };
    },
    onError: (_error, _params, context) => {
      if (context?.previousRows) {
        queryClient.setQueryData(QUERY_KEY, context.previousRows);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  });

  // Optimistically flips the row to "STARTED" so the Run cell swaps to a spinner right away,
  // then reconciles with the server's actual status once the refetch lands.
  const runMutation = useMutation({
    mutationFn: runQueryExportRow,
    onMutate: (id: number) => {
      const previousRows = queryClient.getQueryData<QueryRow[]>(QUERY_KEY);
      queryClient.setQueryData<QueryRow[]>(QUERY_KEY, rows =>
        rows?.map(row => (row.id === id ? { ...row, completed_status: 'STARTED' } : row))
      );
      return { previousRows };
    },
    onError: (e, _id, context) => {
      if (context?.previousRows) {
        queryClient.setQueryData(QUERY_KEY, context.previousRows);
      }
      setError({ message: `Failed to run query. ERROR: ${e.message}`, reportError: e });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    save: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    runQuery: runMutation.mutate
  };
};
